import { useI18n } from "@common/hooks/i18n";
import apiV1 from "@common/lib/api";
import { uuid } from "@common/lib/misc";
import React from "react";
import { type DropzoneState, type FileRejection } from "react-dropzone";
import { getAccept, getMaxSizeInBytes } from "./lib";
import { type UploadSingleMediaPropsBase } from "./types";
import { type WithDropzoneComponentProps } from "./withDropzone";

type Result = DropzoneState & {
  isUploading: boolean;
};
export const useSingleMediaDropdoze = <T extends {}>(
  props: UploadSingleMediaPropsBase<T> & WithDropzoneComponentProps
): Result => {
  const i18n = useI18n();

  const [isUploading, setIsUploading] = React.useState(false);

  const maxSizeMb = props.maxSizeMb ?? 10;
  const res = props.useDropzone({
    accept: getAccept(props.type),
    disabled: props.isDisabled,
    maxFiles: 1,
    maxSize: getMaxSizeInBytes(maxSizeMb),
    onDropRejected: (fileRejections: FileRejection[]) => {
      props.onDropReject(fileRejections, props.maxSizeMb);
    },
    onDropAccepted: async (files: File[]) => {
      const id = uuid();
      props.onStartUploading &&
        props.onStartUploading({
          id,
          name: files[0].name,
          size: files[0].size,
          type: files[0].type
        });

      setIsUploading(true);
      try {
        const result = await apiV1.upload<T[]>(i18n, props.url, files[0]);

        props.onUploaded({
          id,
          name: files[0].name,
          ...result[0]
        });
      } finally {
        setIsUploading(false);
      }
    }
  });

  return {
    ...res,
    isUploading
  };
};
